import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import queriesApi from '../api/queries';
import { getNotes } from '../api/sharedNotes';
import NewsWidget from '../components/news/NewsWidget';
import { format } from 'date-fns';

const DashboardCard = ({ title, children, className }) => {
  return (
    <div className={`p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md transition-colors ${className}`}>
      <h2 className="mb-4 text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">{title}</h2>
      {children}
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [recentQueries, setRecentQueries] = useState([]);
  const [isQueriesLoading, setIsQueriesLoading] = useState(true);
  const [sharedNotes, setSharedNotes] = useState([]);
  const [isNotesLoading, setIsNotesLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch shared notes
        setIsNotesLoading(true);
        const notes = await getNotes(0, 5, null, null); // Get 5 most recent notes
        setSharedNotes(notes.items || []);
        setIsNotesLoading(false);
        
        // Fetch recent queries if user is authenticated
        if (user) {
          setIsQueriesLoading(true);
          const queries = await queriesApi.getQueryHistory(5); // Get 5 most recent queries
          setRecentQueries(queries);
          setIsQueriesLoading(false);
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load some dashboard data. Please try again later.');
        setIsNotesLoading(false);
        setIsQueriesLoading(false);
      }
    };
    
    fetchData();
  }, [user]);
  
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = new Date(timestamp);
    return date.toLocaleString();
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">Dashboard</h1>
      </div>
      
      {/* Welcome message */}
      <DashboardCard title="Welcome" className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/30 border border-primary-100 dark:border-primary-800/50">
        <p className="text-lg text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
          Welcome{user ? `, ${user.full_name || user.username}` : ''}! 
          This dashboard provides quick access to the astronomy database and your query history.
        </p>
        <div className="flex flex-wrap gap-3 mt-4">
          <Link
            to="/query"
            className="px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
          >
            Run a Query
          </Link>
          <Link
            to="/database"
            className="px-4 py-2 text-sm font-medium text-primary-700 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/30 rounded-md hover:bg-primary-200 dark:hover:bg-primary-800/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
          >
            Explore Database
          </Link>
        </div>
      </DashboardCard>
      
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Shared Notes */}
        <DashboardCard title="Recent Shared Notes">
          {isNotesLoading ? (
            <div className="flex justify-center py-4">
              <svg className="w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : sharedNotes.length > 0 ? (
            <div>
              <ul className="space-y-3">
                {sharedNotes.map((note) => (
                  <li key={note.id}>
                    <Link
                      to={`/shared-notes/${note.id}`}
                      className="block p-3 text-sm border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover hover:border-primary-300 dark:hover:border-primary-600 transition-all cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary truncate">
                            {note.title}
                          </span>
                          {note.is_pinned && (
                            <svg className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M4 13V7a4 4 0 018 0v6a4 4 0 01-8 0z" />
                              <path fillRule="evenodd" d="M6 3a2 2 0 00-2 2v1h10V5a2 2 0 00-2-2H6z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <span className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted flex-shrink-0">
                          {format(new Date(note.updated_at), 'MMM d')}
                        </span>
                      </div>
                      <div className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary mb-2">
                        by {note.created_by.full_name}
                      </div>
                      {note.content && (
                        <div className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted line-clamp-2">
                          {note.content.length > 100 ? `${note.content.substring(0, 100)}...` : note.content}
                        </div>
                      )}
                      <div className="mt-2">
                        <span className="text-xs font-medium text-primary-600 dark:text-primary-400">
                          Click to read →
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
              
              <div className="mt-4">
                <Link
                  to="/shared-notes"
                  className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                >
                  View all shared notes →
                </Link>
              </div>
            </div>
          ) : (
            <div className="py-4 text-center">
              <p className="mb-3 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">No shared notes available yet.</p>
              {user && (
                <Link
                  to="/shared-notes"
                  className="px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
                >
                  Create First Note
                </Link>
              )}
            </div>
          )}
          
          {error && (
            <div className="p-2 mt-4 text-sm text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md">
              {error}
            </div>
          )}
        </DashboardCard>
        
        {/* Recent queries */}
        <DashboardCard title="Recent Queries">
          {!user ? (
            <div className="py-4 text-center">
              <p className="mb-3 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Log in to view your query history</p>
              <Link
                to="/login"
                className="px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
              >
                Sign In
              </Link>
            </div>
          ) : isQueriesLoading ? (
            <div className="flex justify-center py-4">
              <svg className="w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : recentQueries.length > 0 ? (
            <div>
              <ul className="space-y-3">
                {recentQueries.map((query) => (
                  <li key={query.id}>
                    <Link
                      to={`/queries/${query.id}`}
                      className="block p-3 text-sm border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover hover:border-primary-300 dark:hover:border-primary-600 transition-all cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          query.status === 'completed' ? 'bg-status-success-light-bg dark:bg-status-success-dark-bg text-status-success-light-text dark:text-status-success-dark-text' : 
                          query.status === 'failed' ? 'bg-status-error-light-bg dark:bg-status-error-dark-bg text-status-error-light-text dark:text-status-error-dark-text' : 
                          'bg-status-warning-light-bg dark:bg-status-warning-dark-bg text-status-warning-light-text dark:text-status-warning-dark-text'
                        }`}>
                          {query.status}
                        </span>
                        <span className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">
                          {formatTimestamp(query.created_at)}
                        </span>
                      </div>
                      <div className="mb-1 font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
                        <div className="overflow-hidden text-ellipsis whitespace-nowrap" style={{ maxWidth: '100%' }}>
                          {query.query_text}
                        </div>
                      </div>
                      {query.execution_time_ms && (
                        <div className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">
                          Execution time: {query.execution_time_ms}ms
                        </div>
                      )}
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-xs font-medium text-primary-600 dark:text-primary-400">
                          Click to view details →
                        </span>
                        <span 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Navigate to run again
                            window.location.href = `/query?q=${encodeURIComponent(query.query_text)}`;
                          }}
                          className="text-xs font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 cursor-pointer"
                        >
                          Run again
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
              
              <div className="mt-4">
                <Link
                  to="/queries/history"
                  className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                >
                  View all queries →
                </Link>
              </div>
            </div>
          ) : (
            <p className="text-ui-light-text-muted dark:text-ui-dark-text-muted">No query history yet. Try running some queries!</p>
          )}
        </DashboardCard>
      </div>
      
      {/* News Widget */}
      {user && (
        <div className="mt-6">
          <div className="h-full">
            <NewsWidget />
          </div>
        </div>
      )}
      
      {/* Quick links */}
      <DashboardCard title="Quick Links">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
          <Link
            to="/query"
            className="flex flex-col items-center p-4 text-center border border-ui-light-border dark:border-ui-dark-border rounded-lg hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
          >
            <svg className="w-8 h-8 mb-2 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Run Query</span>
            <span className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">Execute SQL or ADQL queries</span>
          </Link>
          
          <Link
            to="/database"
            className="flex flex-col items-center p-4 text-center border border-ui-light-border dark:border-ui-dark-border rounded-lg hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
          >
            <svg className="w-8 h-8 mb-2 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
            </svg>
            <span className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Explore Database</span>
            <span className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">Browse schemas and tables</span>
          </Link>
          
          <Link
            to="/queries/history"
            className="flex flex-col items-center p-4 text-center border border-ui-light-border dark:border-ui-dark-border rounded-lg hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
          >
            <svg className="w-8 h-8 mb-2 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Query History</span>
            <span className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">View your past queries</span>
          </Link>
        </div>
      </DashboardCard>
    </div>
  );
};

export default Dashboard;